import { baseApi } from "./baseApi";

export interface AgentGameSlotPublic {
  roleSlug: string;
  roleName: string;
  username: string | null;
  prompt: string | null;
  joined: boolean;
  choice?: string | null;
  hiddenTrait?: string;
}

export interface AgentGamePublic {
  id: string;
  name: string;
  description: string;
  status: string;
  startsAt: string;
  endsAt: string;
  config: {
    maxPromptLength: number;
    choices: string[];
    choiceLabel: string;
    agentName: string;
    slots: AgentGameSlotPublic[];
  };
  agentDeclaredChoice: string | null;
  agentReasoning: string | null;
  declaredAt: string | null;
  scoringResults: Array<{
    userId: string;
    username: string;
    choice: string;
    respectAwarded: number;
  }> | null;
}

export const agentGameApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAgentGameCurrent: builder.query<{ success: boolean; game: AgentGamePublic | null }, void>({
      query: () => "/agent-game/current",
      providesTags: ["AgentGame"],
    }),
    getAgentGameById: builder.query<{ success: boolean; game: AgentGamePublic }, string>({
      query: (id) => `/agent-game/${encodeURIComponent(id)}`,
      providesTags: (_r, _e, id) => [{ type: "AgentGame", id }],
    }),
    getAgentGameHistory: builder.query<{ success: boolean; games: AgentGamePublic[] }, void>({
      query: () => "/agent-game/history",
      providesTags: ["AgentGame"],
    }),
    getAgentGameResults: builder.query<
      {
        success: boolean;
        results: {
          agentDeclaredChoice: string | null;
          agentReasoning: string | null;
          declaredAt: string | null;
          scoringResults: AgentGamePublic["scoringResults"];
          slots: Array<{
            roleSlug: string;
            roleName: string;
            username: string | null;
            prompt: string | null;
            choice: string | null;
          }>;
        };
      },
      string
    >({
      query: (id) => `/agent-game/${encodeURIComponent(id)}/results`,
      providesTags: (_r, _e, id) => [{ type: "AgentGame", id }],
    }),
    joinAgentGame: builder.mutation<
      { success: boolean; slot?: AgentGameSlotPublic; message?: string },
      { gameId: string; roleSlug: string }
    >({
      query: ({ gameId, roleSlug }) => ({
        url: `/agent-game/${encodeURIComponent(gameId)}/join`,
        method: "POST",
        body: { roleSlug },
      }),
      invalidatesTags: ["AgentGame"],
    }),
    setAgentGamePrompt: builder.mutation<
      { success: boolean; message?: string },
      { gameId: string; prompt: string }
    >({
      query: ({ gameId, prompt }) => ({
        url: `/agent-game/${encodeURIComponent(gameId)}/prompt`,
        method: "PUT",
        body: { prompt },
      }),
      invalidatesTags: ["AgentGame"],
    }),
    setAgentGameChoice: builder.mutation<
      { success: boolean; message?: string },
      { gameId: string; choice: string }
    >({
      query: ({ gameId, choice }) => ({
        url: `/agent-game/${encodeURIComponent(gameId)}/choice`,
        method: "POST",
        body: { choice },
      }),
      invalidatesTags: ["AgentGame"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAgentGameCurrentQuery,
  useGetAgentGameByIdQuery,
  useGetAgentGameHistoryQuery,
  useGetAgentGameResultsQuery,
  useJoinAgentGameMutation,
  useSetAgentGamePromptMutation,
  useSetAgentGameChoiceMutation,
} = agentGameApi;
